import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { usePricing } from '../../contexts/PricingContext';
import { EstimatedCostChip } from '../common/EstimatedCostChip';
import { getDocTimestampFromLink } from './utils';

interface PresentationExportStepProps {
  tenderId: string;
  onGeneratePresentation: (
    exportFormat: 'pptx' | 'pdf' | 'html',
    numSlides: number,
    engine?: string
  ) => Promise<void>;
  onGeneratePdf: () => Promise<void>;
  onDownloadPresentation: () => Promise<void>;
  onDownloadPdf: () => Promise<void>;
  onResetPresentation: () => Promise<void>;
  onResetPdf: () => Promise<void>;
  isGeneratingPresentation?: boolean;
  isGeneratingPdf?: boolean;
  presentationLink?: string | undefined;
  pdfLink?: string | undefined;
  presentationError?: string | undefined;
  pdfError?: string | undefined;
  hasSections?: boolean;
}

const PresentationExportStep: React.FC<PresentationExportStepProps> = ({
  onGeneratePresentation,
  onGeneratePdf,
  onDownloadPresentation,
  onDownloadPdf,
  onResetPresentation,
  onResetPdf,
  isGeneratingPresentation = false,
  isGeneratingPdf = false,
  presentationLink,
  pdfLink,
  presentationError,
  pdfError,
  hasSections = false,
}) => {
  const theme = useTheme();
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const [exportFormat, setExportFormat] = useState<'pptx' | 'pdf' | 'html'>('pptx');
  const [numSlides, setNumSlides] = useState<number>(10);
  const [engine, setEngine] = useState<string>(
    (account?.clientSettings?.presentationEngine as string) || 'marp'
  );

  const getRegenButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.greyContrast : theme.palette.greyLight,
    color: enabled ? theme.palette.greyLight : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: 'auto',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': enabled
      ? {
          backgroundColor: theme.palette.bidlogiq.brick,
          color: theme.palette.bidlogiq.white,
          boxShadow: theme.shadows[4],
        }
      : {
          backgroundColor: theme.palette.greyLight,
          boxShadow: theme.shadows[4],
        },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq.orange}`,
    },
    width: '100%',
  });

  const getDownloadButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
    color: enabled ? theme.palette.bidlogiq.white : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: 'auto',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': {
      backgroundColor: enabled ? theme.palette.bidlogiq.rust : theme.palette.greyLight,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq.orange}`,
    },
    width: '100%',
    justifyContent: 'center',
    '& .MuiButton-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1.2,
    },
  });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '1rem' }}>
        Transform your narrative into professionally styled, visually appealing documents. Our
        system automatically applies your company's brand, theme colors, and style guidelines to
        create a polished final product.
      </Typography>

      {!hasSections && (
        <Typography
          variant="body2"
          color={theme.palette.mode === 'dark' ? 'warning.light' : 'warning.dark'}
          sx={{ mb: 3, fontSize: '1rem', fontWeight: 600 }}
        >
          ⚠️ You must generate narrative draft sections in the drafting steps above before you can
          create visual exports.
        </Typography>
      )}

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Presentation Configuration
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="format-engine-select-label">
                      Presentation Format & Engine
                    </InputLabel>
                    <Select
                      labelId="format-engine-select-label"
                      value={
                        engine === 'pure-html'
                          ? exportFormat === 'pdf'
                            ? 'pure-html-pdf'
                            : 'pure-html-html'
                          : engine
                      }
                      label="Presentation Format & Engine"
                      onChange={(e) => {
                        const val = e.target.value as string;
                        if (val === 'pure-html-pdf') {
                          setEngine('pure-html');
                          setExportFormat('pdf');
                        } else if (val === 'pure-html-html') {
                          setEngine('pure-html');
                          setExportFormat('html');
                        } else if (val === 'marp') {
                          setEngine('marp');
                          setExportFormat('pptx');
                        }
                      }}
                      disabled={isGeneratingPresentation}
                    >
                      <MenuItem value="pure-html-html">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            HTML
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'normal' }}
                          >
                            <strong>Best for:</strong> Highest visual quality, fastest generation.
                            Often ready for immediate use. Caveat: Harder to edit.
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="pure-html-pdf">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            HTML (PDF)
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'normal' }}
                          >
                            <strong>Best for:</strong> Fastest generation, digital viewing. Often
                            ready for immediate use (editable with PDF software).
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="marp">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            PPTX (Quality)
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'normal' }}
                          >
                            <strong>Best for:</strong> Highly styled presentations (image-based
                            slides). Will definitely require manual editing.
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '1rem' }}
                    gutterBottom
                  >
                    Target Slides: {numSlides}
                  </Typography>
                  <Slider
                    size="small"
                    value={numSlides}
                    min={5}
                    max={30}
                    step={1}
                    onChange={(_, newValue) => setNumSlides(newValue as number)}
                    disabled={isGeneratingPresentation}
                    valueLabelDisplay="auto"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={() => onGeneratePresentation(exportFormat, numSlides, engine)}
                  disabled={!hasSections || isGeneratingPresentation}
                  startIcon={
                    isGeneratingPresentation ? (
                      <CircularProgress size={16} sx={{ color: theme.palette.bidlogiq.orange }} />
                    ) : (
                      <SlideshowIcon />
                    )
                  }
                  sx={{
                    ...getRegenButtonStyle(theme, !isGeneratingPresentation),
                    flexGrow: 1,
                    ...(presentationError
                      ? {
                          backgroundColor: theme.palette.error.main,
                          color: theme.palette.error.contrastText,
                          '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                          },
                        }
                      : {}),
                  }}
                >
                  {isGeneratingPresentation
                    ? 'Generating Presentation...'
                    : presentationError
                      ? 'Error'
                      : 'Generate Presentation'}
                </Button>
                {(isGeneratingPresentation || !!presentationError) && (
                  <Button
                    onClick={onResetPresentation}
                    variant="outlined"
                    color="error"
                    sx={{ minWidth: 'auto', py: 0.5, px: 2 }}
                    title={presentationError ? 'Reset error state' : 'Reset stuck generation'}
                  >
                    Reset
                  </Button>
                )}
                <Tooltip
                  title="Exact billing depends on the length and complexity of the tender documents and provided context."
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <EstimatedCostChip
                    estimate={
                      pricingSettings?.estimates?.presentation_export || 'Est. $10.00 – $50.00'
                    }
                  />
                </Tooltip>
              </Box>
            </Box>

            <Button
              startIcon={<DownloadIcon />}
              onClick={onDownloadPresentation}
              disabled={!presentationLink}
              sx={getDownloadButtonStyle(theme, !!presentationLink)}
            >
              <Box
                sx={{
                  lineHeight: 1.2,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: '1rem',
                }}
              >
                Download presentation
                {presentationLink && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      opacity: 0.9,
                      textTransform: 'none',
                    }}
                  >
                    {`, ${getDocTimestampFromLink(presentationLink, undefined)}`}
                  </Box>
                )}
              </Box>
            </Button>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontSize: '0.875rem' }}>
              <em>
                Note: The visual document generation feature is currently experimental. If the PDF
                fails to generate, please contact{' '}
                <Link href="mailto:support@bidlogiq.ai" color="inherit">
                  support@bidlogiq.ai
                </Link>
                .
              </em>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onGeneratePdf}
                disabled={!hasSections || isGeneratingPdf}
                startIcon={
                  isGeneratingPdf ? (
                    <CircularProgress size={16} sx={{ color: theme.palette.bidlogiq.orange }} />
                  ) : (
                    <PictureAsPdfIcon />
                  )
                }
                sx={{
                  ...getRegenButtonStyle(theme, !isGeneratingPdf),
                  flexGrow: 1,
                  ...(pdfError
                    ? {
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.error.dark,
                        },
                      }
                    : {}),
                }}
              >
                {isGeneratingPdf
                  ? 'Generating PDF...'
                  : pdfError
                    ? `ERROR: ${pdfError}`
                    : 'Generate PDF'}
              </Button>
              {(isGeneratingPdf || !!pdfError) && (
                <Button
                  onClick={onResetPdf}
                  variant="outlined"
                  color="error"
                  sx={{ minWidth: 'auto', py: 0.5, px: 2 }}
                  title={pdfError ? 'Reset error state' : 'Reset stuck generation'}
                >
                  Reset
                </Button>
              )}
              <Tooltip
                title="Exact billing depends on the length and complexity of the tender documents and provided context."
                slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
              >
                <EstimatedCostChip
                  estimate={pricingSettings?.estimates?.pdf_export || 'Est. $10.00 – $30.00'}
                />
              </Tooltip>
            </Box>

            <Button
              startIcon={<DownloadIcon />}
              onClick={onDownloadPdf}
              disabled={!pdfLink}
              sx={getDownloadButtonStyle(theme, !!pdfLink)}
            >
              <Box
                sx={{
                  lineHeight: 1.2,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: '1rem',
                }}
              >
                Download PDF
                {pdfLink && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      opacity: 0.9,
                      textTransform: 'none',
                    }}
                  >
                    {`, ${getDocTimestampFromLink(pdfLink, undefined)}`}
                  </Box>
                )}
              </Box>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PresentationExportStep;
